/**
 * src/pages/CriticalThinking/sections/BeliefRevisionSection.tsx
 * Section 5: BELIEF REVISION EXERCISE (id="belief-revision")
 * & Section 6: "What Would Change My Mind?" Practice.
 * Interactive confidence slider (0-100%) through 4 stages of evidence reveals.
 * Shows personal trajectory mini-chart and explains proportionate updates.
 * All confidence data stays strictly in component memory; reset button included.
 */

import React, { useState } from "react";
import { KSection } from "@/components/knowledge/KSection";
import { ToolFrame } from "@/components/knowledge/ToolFrame";
import { Feedback } from "@/components/knowledge/Feedback";
import { Deeper } from "@/components/knowledge/Deeper";
import { BELIEF_SCENARIO, MIND_CHANGE_ITEMS, type EvidenceStage } from "../data/belief.data";
import { AlertCircle, ArrowRight, Check, CheckCircle2, Compass, RotateCcw, Sparkles, TrendingDown } from "lucide-react";
export function BeliefRevisionSection() {
  const scenario = BELIEF_SCENARIO;

  // Active stage (0 to 4: 0 = initial context, 1..4 = stage 1 to 4)
  const [currentStageIndex, setCurrentStageIndex] = useState(0);

  // Confidence recorded by visitor at each milestone (0 = initial, 1..4 = after stages)
  const [confidenceTrajectory, setConfidenceTrajectory] = useState<number[]>([55]);
  const [activeSliderVal, setActiveSliderVal] = useState<number>(55);

  // Practice section 6 selections
  const [mindChangeAnswers, setMindChangeAnswers] = useState<Record<string, string>>({});
  const handleConfidenceSubmit = () => {
    const nextTrajectory = [...confidenceTrajectory];
    nextTrajectory[currentStageIndex] = activeSliderVal;
    setConfidenceTrajectory(nextTrajectory);
    if (currentStageIndex < scenario.stages.length) {
      setCurrentStageIndex(prev => prev + 1);
      // Pre-set slider to whatever was recorded or keep previous
      setActiveSliderVal(nextTrajectory[currentStageIndex + 1] ?? activeSliderVal);
    }
  };
  const resetRevisionExercise = () => {
    setCurrentStageIndex(0);
    setConfidenceTrajectory([55]);
    setActiveSliderVal(55);
  };
  return <KSection id="belief-revision" eyebrow="Interactive Instrument C" title="Calibrating Confidence & Belief Revision" intro="Rationality is not about picking a side and defending it to the death. It is the practice of adjusting your confidence in proportion to the quality and direction of newly acquired evidence. Updating a belief in the face of counter-evidence is the hallmark of intellectual strength.">
      <ToolFrame title="Dynamic Bayesian Calibration Simulator" purpose="Allows you to test your willingness to update confidence across four chronological stages of a multi-week forensic engineering investigation. Displays your confidence trajectory against calibrated scientific updates." limits="Real Bayesian probability calculations require exact prior likelihoods and likelihood ratios. This exercise simulates proportional qualitative updating on an intuitive percentage scale. All slider data lives exclusively in component state.">
        {/* Scenario Header */}
        <div className="p-4 sm:p-5 rounded-lg border border-border/80 bg-card/50 mb-6 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap text-xs font-mono">
            <span className="text-primary font-bold">CASE STUDY: THE OBSERVATORY SIGNAL MYSTERY</span>
            <button type="button" onClick={resetRevisionExercise} className="text-muted-foreground hover:text-foreground flex items-center gap-1 cursor-pointer min-h-[44px]">
              <RotateCcw className="size-3.5" />
              <span>Reset Exercise</span>
            </button>
          </div>

          <h3 className="font-serif text-base sm:text-lg font-bold text-foreground">
            {scenario.coreHypothesis}
          </h3>

          <p className="font-sans text-xs sm:text-sm text-foreground/90 leading-relaxed">
            {scenario.initialContext}
          </p>
        </div>

        {/* Current Active Stage or Final Trajectory */}
        {currentStageIndex < scenario.stages.length ? <div className="space-y-6">
            {/* Show all stages up to current */}
            {scenario.stages.slice(0, currentStageIndex + 1).map((stage, idx) => <div key={stage.stageNumber} className={`p-4 sm:p-5 rounded-lg border transition-all ${idx === currentStageIndex ? "border-primary/60 bg-card/60 shadow-[0_0_16px_var(--glow-primary)]" : "border-border/40 bg-card/30 opacity-75"}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="font-serif text-sm font-bold text-foreground">
                    {stage.stageTitle}
                  </span>
                  <span className="font-mono text-xs text-primary">
                    Stage {stage.stageNumber} of 4
                  </span>
                </div>

                <p className="font-sans text-xs sm:text-sm text-foreground/90 leading-relaxed mb-3">
                  {stage.evidenceRevealed}
                </p>

                <div className="text-xs font-sans text-muted-foreground p-3 rounded bg-card/70 border border-border/40 mb-3">
                  <strong className="text-foreground font-mono block mb-0.5">
                    Source & Reliability Audit:
                  </strong>
                  {stage.sourceReliabilityNote}
                </div>

                {idx < currentStageIndex && <div className="text-xs font-mono text-primary flex items-center justify-between pt-2 border-t border-border/30">
                    <span>Your recorded confidence at this stage:</span>
                    <span className="font-bold text-sm">{confidenceTrajectory[idx]}%</span>
                  </div>}
              </div>)}

            {/* Slider Control for the current stage */}
            <div className="p-4 sm:p-5 rounded-lg border border-primary/50 bg-card/40 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-serif text-sm font-bold text-foreground">
                  Set Your Confidence in the Telecom Tower Hypothesis:
                </span>
                <span className="font-mono text-lg font-bold text-primary">
                  {activeSliderVal}%
                </span>
              </div>

              <input type="range" min="0" max="100" step="1" value={activeSliderVal} onChange={e => setActiveSliderVal(parseInt(e.target.value, 10))} className="crit-slider w-full cursor-pointer" aria-label="Confidence percentage slider" />

              <div className="flex justify-between text-[11px] font-mono text-muted-foreground">
                <span>0% (Impossible)</span>
                <span>25% (Unlikely)</span>
                <span>50% (Equally likely)</span>
                <span>75% (Probable)</span>
                <span>100% (Certain)</span>
              </div>

              <div className="pt-2">
                <button type="button" onClick={handleConfidenceSubmit} className="ql-neon-btn w-full sm:w-auto px-6 py-3 text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer">
                  <span>Lock In Stage {currentStageIndex + 1} Assessment</span>
                  <ArrowRight className="size-4" />
                </button>
              </div>
            </div>
          </div> : (/* Final Trajectory Analysis View */
      <div className="space-y-6">
            <div className="p-4 sm:p-6 rounded-lg border border-primary/60 bg-card/70 space-y-5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-serif text-base font-bold text-foreground flex items-center gap-2">
                  <Compass className="size-5 text-primary" />
                  Your Belief Revision Trajectory
                </span>
                <span className="font-mono text-xs text-primary">
                  4 Stages Completed
                </span>
              </div>

              {/* Trajectory visualization columns */}
              <div className="grid grid-cols-4 gap-2 sm:gap-4 pt-4 pb-2 border-b border-border/60">
                {scenario.stages.map((st, idx) => {
              const userVal = confidenceTrajectory[idx] ?? 0;
              const [recLow, recHigh] = st.recommendedConfidenceRange;
              return <div key={idx} className="crit-trajectory-col">
                      <span className="font-mono text-xs font-bold text-primary">
                        {userVal}%
                      </span>
                      <div className="crit-trajectory-track">
                        <div className="crit-trajectory-fill" style={{
                    height: `${userVal}%`
                  }} />
                      </div>
                      <span className="font-mono text-[10px] sm:text-xs text-muted-foreground text-center">
                        Stage {st.stageNumber}
                      </span>
                      <span className="text-[9px] font-mono text-primary/80 text-center">
                        Rec: {recLow}-{recHigh}%
                      </span>
                    </div>;
            })}
              </div>

              {/* Stage-by-Stage Expert Benchmark Comparison */}
              <div className="space-y-3">
                <h4 className="font-serif text-sm font-bold text-foreground">
                  Proportionate Update Benchmark Across the Stages:
                </h4>
                {scenario.stages.map((st, idx) => {
              const userVal = confidenceTrajectory[idx] ?? 0;
              const [recLow, recHigh] = st.recommendedConfidenceRange;
              const isWithinRange = userVal >= recLow && userVal <= recHigh;
              return <div key={idx} className="p-3.5 rounded border border-border/40 bg-card/40 text-xs font-sans space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-serif font-bold text-foreground">
                          {st.stageTitle}
                        </span>
                        <span className="font-mono">
                          You: <strong className="text-primary">{userVal}%</strong> | Expected:{" "}
                          <strong className="text-muted-foreground">{recLow}-{recHigh}%</strong>
                        </span>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {st.expertRationale}
                      </p>
                    </div>;
            })}
              </div>

              <Feedback tone="good" title="Core Insight: Updating is Not Weakness">
                <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed">
                  {scenario.synthesisTakeaway}
                </p>
              </Feedback>

              <button type="button" onClick={resetRevisionExercise} className="ql-glass-btn px-4 py-2 text-xs font-mono text-foreground flex items-center gap-1.5 cursor-pointer">
                <RotateCcw className="size-3.5" />
                <span>Repeat Belief Revision Simulation</span>
              </button>
            </div>
          </div>)}

        {/* Section 6: "What Would Change My Mind?" Practice */}
        <div className="mt-12 pt-8 border-t border-border space-y-6">
          <div className="space-y-2">
            <span className="ql-eyebrow">Falsifiability in Action</span>
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
              "What Would Change My Mind?" Practice
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed max-w-3xl">
              The hallmark of a genuinely empirical belief is that you can state what observable event would force you to lower your confidence. If no conceivable evidence could dislodge an assertion, it is a dogma or identity marker, not a factual hypothesis.
            </p>
          </div>

          <div className="space-y-4">
            {MIND_CHANGE_ITEMS.map(item => <div key={item.id} className="p-4 sm:p-5 rounded-lg border border-border/70 bg-card/30 space-y-3">
                <h4 className="font-serif text-sm font-bold text-foreground">
                  {item.claim}
                </h4>
                <p className="text-xs text-muted-foreground font-sans">
                  Select the observation that would genuinely provide evidentiary grounds to lower confidence:
                </p>

                <div className="space-y-2">
                  {item.potentialObservations.map(obs => {
                const isSelected = mindChangeAnswers[item.id] === obs.id;
                return <button key={obs.id} type="button" onClick={() => setMindChangeAnswers(prev => ({
                  ...prev,
                  [item.id]: obs.id
                }))} className={`crit-option-btn ${isSelected ? "selected" : ""}`}>
                        <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                          {isSelected && <Check className="size-3 text-primary" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-foreground">{obs.text}</p>
                          {isSelected && <p className={`text-xs mt-1 font-mono ${obs.isGenuineLowerer ? "text-primary" : "text-amber-400"}`}>
                              {obs.explanation}
                            </p>}
                        </div>
                      </button>;
              })}
                </div>
              </div>)}
          </div>
        </div>
      </ToolFrame>
    </KSection>;
}